import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import html2canvas from "html2canvas";
import { Box } from "@material-ui/core";

// import DownloadIcon from "@mui/icons-material/Download";

// 1. TypeScript Interfaces
interface LocationData {
  display_name: string;
  address?: Record<string, string>;
}

interface SwmPhotosResponse {
  firstPhoto: string;
  secondPhoto: string;
}

const mapContainerStyle: React.CSSProperties = {
  width: "80px",
  height: "80px",
  borderRadius: "4px",
};

const center = {
  lat: 22.586211,
  lng: 88.4265787,
};

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  gestureHandling: "none",
  styles: [
    { featureType: "all", elementType: "all", stylers: [{ saturation: -20 }] },
  ],
};

export function GeoTaggedImage({
  images,
  location,
  segregatted,
}: {
  images?: string;
  location?: any;
  segregatted?: boolean;
}) {
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [swmPhotos, setSwmPhotos] = useState<SwmPhotosResponse | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const captureRef = useRef<HTMLDivElement>(null);

  console.log("Received Location Prop: ", location);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBEXQ990KC3e-1GklhfLENNavc40uRLvKk",
  });

  useEffect(() => {
    fetch(
      `https://osm.distronix.in/reverse?lat=${center.lat}&lon=${center.lng}&format=json`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    )
      .then((res) => res.json())
      .then((data: LocationData) => setLocationData(data))
      .catch((err) => console.error(err));
  }, []);

  const photoUrl = useMemo(() => {
    if (!images) return "";
    if (images === "null") return "";
    return images?.includes(",") ? images.split(",")[0]?.trim() || "" : images;
  }, [images]);

  const firstPhotoSrc = useMemo(() => {
    const raw = swmPhotos?.firstPhoto;
    if (!raw) return "";
    if (raw.startsWith("data:")) return raw;
    return `data:image/jpeg;base64,${raw}`;
  }, [swmPhotos?.firstPhoto]);

  const secondPhotoSrc = useMemo(() => {
    const raw = swmPhotos?.secondPhoto;
    if (!raw) return "";
    if (raw.startsWith("data:")) return raw;
    return `data:image/jpeg;base64,${raw}`;
  }, [swmPhotos?.secondPhoto]);

  useEffect(() => {
    if (!photoUrl) return;
    setSwmPhotos(null);

    const apiUrl = `https://iccc.ntkiccc.in/newtownkolkata/swm/photos?ack=${encodeURIComponent(
      photoUrl,
    )}`;

    fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // "Allow-Control-Allow-Origin": "*",
      },
    })
      .then((res) => res.json())
      .then((data: SwmPhotosResponse) => {
        console.log("SWM photos data: ", data);
        setSwmPhotos(data);
      })
      .catch((err) => console.error("SWM photos fetch failed:", err));
  }, [photoUrl]);

  const firstDisplaySrc = firstPhotoSrc || photoUrl || "";
  const secondDisplaySrc = secondPhotoSrc || photoUrl || "";

  const handleDownload = async () => {
    if (!captureRef.current) return;
    setIsDownloading(true);

    try {
      // Small delay ensures map tiles render fully before capture
      await new Promise((resolve) => setTimeout(resolve, 800));

      const canvas = await html2canvas(captureRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: null,
        ignoreElements: (element) => element.classList.contains("gm-style-cc"),
      });

      const image = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = image;
      link.download = `site-capture-${Date.now()}.png`;
      link.click();
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed. Check console for details.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* CAPTURE ZONE */}
      <div
        ref={captureRef}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: segregatted ? "53vw" : "100%", // two-photo view
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "12px",
        }}
      >
        {segregatted ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              borderRadius: "12px",
            }}
          >
            {[firstDisplaySrc, secondDisplaySrc].map((src, index) => (
              <div
                key={index}
                style={{
                  position: "relative",
                  flex: 1,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <img
                  key={index}
                  src={src}
                  alt={`Site Capture ${index + 1}`}
                  style={{
                    width: "100%",
                    height: "560px",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "6px",
                    left: "6px",
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    borderRadius: "8px",
                    backgroundColor: "rgba(0, 0, 0, 0.55)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    maxWidth: "345px",
                    color: "#ffffff",
                    backdropFilter: "blur(2px)",
                  }}
                >
                  {/* Map Square */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      overflow: "hidden",
                      borderRadius: "2px",
                      width: "80px",
                      height: "80px",
                    }}
                  >
                    {isLoaded ? (
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={center}
                        zoom={15}
                        options={mapOptions}
                      >
                        <Marker
                          position={center}
                          icon={{
                            url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                            scaledSize: window.google
                              ? new window.google.maps.Size(20, 20)
                              : undefined,
                          }}
                        />
                      </GoogleMap>
                    ) : (
                      <Box sx={{ ...mapContainerStyle, bgcolor: "#1f2937" }} />
                    )}
                  </Box>

                  {/* Metadata Text */}
                  <Box
                    sx={{
                      ml: 1.5,
                      overflow: "hidden",
                      textAlign: "left",
                      mb: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "4px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "9px",
                          fontWeight: "bold",
                          padding: "0px 4px",
                          borderRadius: "2px",
                          textTransform: "uppercase",
                          letterSpacing: "-0.05em",
                          backgroundColor: "#10b981",
                          color: "#000000",
                          lineHeight: 1.2,
                        }}
                      >
                        GPS
                      </p>
                      <p
                        style={{
                          fontSize: "10px",
                          fontFamily: "monospace",
                          color: "#d1d5db",
                        }}
                      >
                        &nbsp;{center.lat.toFixed(5)}°N, {center.lng.toFixed(5)}
                        °E
                      </p>
                    </div>

                    <p
                      style={{
                        fontSize: "10px",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: "#f3f4f6",
                        marginBottom: "2px",
                      }}
                    >
                      {locationData?.display_name || "Fetching address..."}
                    </p>

                    <p
                      style={{
                        fontSize: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.15em",
                        fontWeight: "bold",
                        paddingTop: "4px",
                        color: "#9ca3af",
                        borderTop: "1px solid rgba(255, 255, 255, 0.15)",
                      }}
                    >
                      Captured via NKDA SWM SCANNER
                    </p>
                  </Box>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              position: "relative",
              borderRadius: "12px",
              overflow: "hidden",
              width: 400,
            }}
          >
            <img
              src={firstDisplaySrc || images || ""} // Single photo fallback
              alt="Site Capture"
              style={{
                width: "100%",
                height: "560px",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: "17px",
                left: "6px",
                display: "flex",
                alignItems: "center",
                padding: "4px",
                borderRadius: "8px",
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                maxWidth: "355px",
                color: "#ffffff",
                backdropFilter: "blur(2px)",
              }}
            >
              {/* Map Square */}
              <Box
                sx={{
                  flexShrink: 0,
                  overflow: "hidden",
                  borderRadius: "2px",
                  width: "80px",
                  height: "80px",
                }}
              >
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={center}
                    zoom={15}
                    options={mapOptions}
                  >
                    <Marker
                      position={center}
                      icon={{
                        url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                        scaledSize: window.google
                          ? new window.google.maps.Size(20, 20)
                          : undefined,
                      }}
                    />
                  </GoogleMap>
                ) : (
                  <Box sx={{ ...mapContainerStyle, bgcolor: "#1f2937" }} />
                )}
              </Box>

              {/* Metadata Text */}
              <Box
                sx={{ ml: 1.5, overflow: "hidden", textAlign: "left", mb: 0 }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "4px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "9px",
                      fontWeight: "bold",
                      padding: "0px 4px",
                      borderRadius: "2px",
                      textTransform: "uppercase",
                      letterSpacing: "-0.05em",
                      backgroundColor: "#10b981",
                      color: "#000000",
                      lineHeight: 1.2,
                    }}
                  >
                    GPS
                  </p>
                  <p
                    style={{
                      fontSize: "10px",
                      fontFamily: "monospace",
                      color: "#d1d5db",
                    }}
                  >
                    &nbsp;{center.lat.toFixed(5)}°N, {center.lng.toFixed(5)}°E
                  </p>
                </div>

                <p
                  style={{
                    fontSize: "10px",
                    fontWeight: 500,
                    lineHeight: 1.2,
                    color: "#f3f4f6",
                    marginBottom: "2px",
                  }}
                >
                  {locationData?.display_name || "Fetching address..."}
                </p>

                <p
                  style={{
                    fontSize: "8px",
                    textTransform: "uppercase",
                    letterSpacing: "0.15em",
                    fontWeight: "bold",
                    paddingTop: "4px",
                    color: "#9ca3af",
                    borderTop: "1px solid rgba(255, 255, 255, 0.15)",
                  }}
                >
                  Captured via NKDA SWM SCANNER
                </p>
              </Box>
            </div>
          </div>
        )}

        {/* Geotag Overlay */}
      </div>

      {/* CONTROLS */}
      {/* <button
        color="primary"
        onClick={handleDownload}
        disabled={isDownloading}
        // startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
        style={{
          padding: "12px 24px",
          fontWeight: 600,
          textTransform: "none",
          backgroundColor: "#4f46e5", // Indigo 600
          color: "#ffffff",
          border: "none",
          borderRadius: "4px",
          cursor: isDownloading ? "default" : "pointer",
          marginTop: "16px",
        }}
      >
        {isDownloading ? "Processing..." : "Download Capture"}
      </button> */}

      {/* Global Styles for Google Maps Cleanup */}
      <style>{`
        .gm-style-logo, .gm-style-cc, .gmnoprint { display: none !important; }
      `}</style>
    </div>
  );
}

export default GeoTaggedImage;
